'use client';
import { Analytics } from '@cobliteam/analytics';
import type { UserPermission } from '@cobliteam/common-types';
import { usePermissions } from '@cobliteam/hooks';
import { cn } from '@cobliteam/ui/utils';
import { usePrevious } from 'ahooks';
import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './Sidebar';

import type { FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Icon from '@cobliteam/ui/icons';
import type { SidebarDef } from '../AppSidebar.d';
interface NavMainProps {
  sidebarDef: SidebarDef[];
  featureFlags: FeatureFlags;
}

const initialOpenGroups = (sidebarDef: SidebarDef[], location: any) => {
  const initialState: { [key: string]: boolean } = {};
  for (const nav of sidebarDef ?? []) {
    for (const section of nav.section ?? []) {
      const isActive = section?.group?.some((group) => {
        const isActiveGroup = location.pathname.startsWith(group.url);
        const isActiveSection = location.pathname.startsWith(section.url);
        return isActiveGroup || isActiveSection;
      }); // Only open the section if it contains the current path
      initialState[section.title] = isActive ?? false;
    }
  }
  return initialState;
};

const filterSidebarWithPermission = (sidebarDef: SidebarDef[], isAccessAllowed: (permission: UserPermission) => boolean) => {
  const sidebarDefWithPermission = sidebarDef.reduce<SidebarDef[]>((acc, nav) => {
    if (!nav.permission) return acc;

    if (nav.section) {
      const sectionsWithPermission = nav.section.filter((section) => {
        section.group = section?.group?.filter((group) => isAccessAllowed(group.permission));
        return isAccessAllowed(section.permission);
      });
      if (sectionsWithPermission?.length > 0) acc.push({ ...nav, section: sectionsWithPermission });
      return acc;
    }

    if (isAccessAllowed(nav.permission)) acc.push(nav);

    return acc;
  }, []);
  return sidebarDefWithPermission;
};

const filterSidebarWithFeatureFlag = (sidebarDef: SidebarDef[], featureFlags: FeatureFlags) => {
  const isVisible = (featureFlag?: keyof FeatureFlags) => featureFlag === undefined || featureFlags[featureFlag] === true;
  const sidebarDefWithFeatureFlag = sidebarDef.reduce<SidebarDef[]>((acc, nav) => {
    const sectionsWithFeatureFlag = nav.section?.filter((section) => {
      section.group = section.group?.filter((group) => isVisible(group?.featureFlag));
      return isVisible(section.featureFlag);
    });

    if (sectionsWithFeatureFlag?.length) {
      acc.push({ ...nav, section: sectionsWithFeatureFlag });
      return acc;
    }

    if (isVisible(nav?.featureFlag)) acc.push(nav);

    return acc;
  }, []);
  return sidebarDefWithFeatureFlag;
};

export function SidebarMenu({ sidebarDef, featureFlags }: NavMainProps) {
  const location = useLocation();
  const prevPathname = usePrevious(location.pathname);
  const { isAccessAllowed } = usePermissions();
  const { state, openMobile, setOpenMobile } = useSidebar();
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>(initialOpenGroups(sidebarDef, location));
  const isCollapsed = state === 'collapsed';
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);

  const sidebarDefWithPermission = filterSidebarWithPermission(sidebarDef, isAccessAllowed);
  const sidebarDefWithFeatureFlagAndPermission = filterSidebarWithFeatureFlag(sidebarDefWithPermission, featureFlags);

  const onDropdownChange = (title: string, value: boolean) => {
    setOpen(() => {
      const newState: { [key: string]: boolean } = {};
      newState[title] = value;
      return newState;
    });
  };

  const onOpenGroup = (title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', { title, isOpen });
    setOpenGroups(() => {
      const newState: { [key: string]: boolean } = {};
      newState[title] = isOpen;
      return newState;
    });
  };

  const handleMouseEnter = (title: string) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    onDropdownChange(title, true);
  };

  const handleMouseLeave = (title: string) => {
    const timeout = setTimeout(() => {
      onDropdownChange(title, false);
    }, 200); // Add a small delay before closing
    setCloseTimeout(timeout);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [closeTimeout]);

  // reset the open groups when the pathname changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (prevPathname && prevPathname !== location.pathname) {
      setOpenGroups((prev) => {
        const newState = { ...prev };
        // close all open groups
        for (const key in newState) newState[key] = false;

        // reset the open groups to the initial state
        const initialState = initialOpenGroups(sidebarDefWithFeatureFlagAndPermission, location);
        for (const key in initialState) newState[key] = initialState[key];
        return newState;
      });
    }
  }, [prevPathname, location]);

  return (
    <>
      {sidebarDefWithFeatureFlagAndPermission?.map((nav, index) => {
        // if the nav item is not a group, show a simple menu item
        if (!nav.section) {
          const isActive = location.pathname === nav.url;
          return (
            <SidebarGroup key={nav.title}>
              <SidebarMenuBase>
                <SidebarMenuItem>
                  <Link to={nav.url ?? '#'} onClick={() => Analytics.track('Sidebar - Navigate', { destination: nav.title })}>
                    <SidebarMenuButton tooltip={nav.title} size='sm' className='text-sidebar-menu' isActive={isActive}>
                      {nav.icon}
                      <span>{nav.title}</span>
                    </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>
              </SidebarMenuBase>
            </SidebarGroup>
          );
        }

        // when collapsed, show a dropdown menu
        if (isCollapsed && !openMobile) {
          const urlsOfGroup = nav.section?.flatMap((section) => section.group?.map((group) => group.url) || section.url);
          const isActive = urlsOfGroup?.some((url) => location.pathname.includes(url ?? ''));
          return (
            <SidebarGroup key={nav.title} className='sidebar-group' onMouseEnter={() => handleMouseEnter(nav.title)} onMouseLeave={() => handleMouseLeave(nav.title)}>
              <DropdownMenu key={nav.title} modal={false} open={open[nav.title]}>
                <SidebarMenuBase>
                  <SidebarMenuItem>
                    <DropdownMenu.Trigger asChild className='text-sidebar-menu'>
                      <SidebarMenuButton size='sm' className='mt-2' isActive={isActive}>
                        {nav.icon}
                        <span>{nav.title}</span>
                      </SidebarMenuButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content
                      side='right'
                      align='start'
                      sideOffset={10}
                      className={cn('flex flex-col rounded-md border-2 border-gray-a4 bg-gray-12 text-gray-5')}
                      onMouseEnter={() => handleMouseEnter(nav.title)}
                      onMouseLeave={() => handleMouseLeave(nav.title)}
                    >
                      {/* Section title */}
                      <div className='flex flex-col px-2 pt-2 text-gray-8 text-xs'>{nav.title}</div>

                      {/* Section items */}
                      {nav.section?.map((section) => {
                        const isActive = location.pathname.includes(section.url ?? '');
                        if (section.hideGroup) return null;
                        return (
                          <Fragment key={section.title}>
                            {section?.group ? (
                              <Collapsible
                                key={`${section.title}-${openGroups[section.title]}-${index}`}
                                className='group/collapsible'
                                open={openGroups[section.title]}
                                onOpenChange={(isOpen) => onOpenGroup(section.title, isOpen)}
                              >
                                <Fragment key={section.title}>
                                  <SidebarGroupLabel asChild>
                                    <CollapsibleTrigger className='w-full text-sidebar-menu hover:bg-sidebar-accent hover:text-sidebar-accent-foreground '>
                                      <div className='flex w-full items-center justify-between gap-2'>
                                        <div className='flex items-center gap-2'>
                                          {section.icon}
                                          {section.title}
                                        </div>
                                        <Icon name='chevron-down' className='ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180' />
                                      </div>
                                    </CollapsibleTrigger>
                                  </SidebarGroupLabel>
                                  <CollapsibleContent>
                                    <SidebarGroupContent>
                                      <SidebarMenuSub>
                                        {section?.group?.map((group) => {
                                          const isActive = location.pathname.startsWith(group.url);
                                          return (
                                            <SidebarMenuSubItem key={group.title}>
                                              <SidebarMenuSubButton
                                                asChild
                                                isActive={isActive}
                                                onClick={() => {
                                                  setOpen({ ...open, [nav.title]: false });
                                                }}
                                              >
                                                <Link to={group.url} onClick={() => Analytics.track('Sidebar - Navigate', { destination: group.title })}>
                                                  {group.title}
                                                </Link>
                                              </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                          );
                                        })}
                                      </SidebarMenuSub>
                                    </SidebarGroupContent>
                                  </CollapsibleContent>
                                </Fragment>
                              </Collapsible>
                            ) : (
                              <SidebarMenuBase>
                                <SidebarMenuItem>
                                  <Link to={section.redirect || section.url || '#'} onClick={() => Analytics.track('Sidebar - Navigate', { destination: section.title })}>
                                    <SidebarMenuButton size='sm' className='text-sidebar-menu' isActive={isActive}>
                                      {section?.icon}
                                      <span>{section.title}</span>
                                    </SidebarMenuButton>
                                  </Link>
                                </SidebarMenuItem>
                              </SidebarMenuBase>
                            )}
                          </Fragment>
                        );
                      })}
                    </DropdownMenu.Content>
                  </SidebarMenuItem>
                </SidebarMenuBase>
              </DropdownMenu>
            </SidebarGroup>
          );
        }

        // when not collapsed
        return (
          <SidebarGroup key={nav.title} className={cn('mt-2', index > 1 && 'mt-4')}>
            <SidebarGroupLabel className='!text-gray-8 text-sidebar-section'>{nav.title}</SidebarGroupLabel>
            {nav.section?.map((section, index) => {
              const isActive = location.pathname.startsWith(section.url ?? '');
              return (
                <Fragment key={section.title}>
                  {section?.group && !section.hideGroup ? (
                    <SidebarGroup key={section.title}>
                      <Collapsible
                        key={`${section.title}-${openGroups[section.title]}-${index}`}
                        className='group/collapsible'
                        open={openGroups[section.title]}
                        onOpenChange={(isOpen) => {
                          onOpenGroup(section.title, isOpen);
                        }}
                      >
                        <SidebarGroupLabel asChild>
                          <CollapsibleTrigger className='w-full text-sidebar-menu hover:bg-sidebar-accent hover:text-sidebar-accent-foreground '>
                            <div className='flex w-full items-center justify-between gap-2 '>
                              <div className='flex items-center gap-2'>
                                {section.icon}
                                {section.title}
                              </div>
                              <Icon name='chevron-down' className='ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180' />
                            </div>
                          </CollapsibleTrigger>
                        </SidebarGroupLabel>
                        <CollapsibleContent>
                          <SidebarGroupContent>
                            <SidebarMenuSub>
                              {section?.group?.map((group) => {
                                const isActive = location.pathname.startsWith(group.url);
                                const goTo = group.redirect || group.url || '#';
                                return (
                                  <SidebarMenuSubItem key={group.title}>
                                    <SidebarMenuSubButton asChild isActive={isActive} onClick={() => setOpenMobile(false)}>
                                      <Link to={goTo} onClick={() => Analytics.track('Sidebar - Navigate', { destination: group.title })}>
                                        {group.title}
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </SidebarGroupContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </SidebarGroup>
                  ) : (
                    <SidebarGroup key={section.title}>
                      <SidebarMenuBase>
                        <SidebarMenuItem>
                          <Link to={section.redirect || section.url || '#'} onClick={() => Analytics.track('Sidebar - Navigate', { destination: section.title })}>
                            <SidebarMenuButton tooltip={section.title} size='sm' className='text-sidebar-menu' isActive={isActive} onClick={() => setOpenMobile(false)}>
                              {section?.icon}
                              <span>{section.title}</span>
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      </SidebarMenuBase>
                    </SidebarGroup>
                  )}
                </Fragment>
              );
            })}
          </SidebarGroup>
        );
      })}
    </>
  );
}
